/* Stage 5 — Service Package Template Editor (Admin) — V2 Final UX
   Correction (post-review).

   Human-facing controls replace raw schema editing throughout.  The
   underlying A8 template/version storage is UNCHANGED — every raw
   field this UI hides (`item_id`, `condition_id`, `gate_id`, legacy
   `action_type` enums, `scope_variables.key/type/default`, comma-
   separated pricing/contract methods, activation/completion
   condition rows) remains 100% preserved in the stored draft.  When
   an existing template row lacks the humanised field name, the
   editor derives the human label from whichever raw field is
   populated so nothing renders as [object Object].

   Immutable Package Code + Template Type + published versions remain
   unchanged.  Publishing produces a new immutable version and calls
   the accepted A8 engine.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { usePageMeta } from "../../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- Human-facing enums ----------
// V2 Final UX §6 — client action response type mapping.  Human labels
// are what the operator sees; canonical backend value is stored.
const CLIENT_ACTION_TYPES = [
    { value: "approval_confirmation", label: "Acknowledgement" },
    { value: "file_upload",           label: "Upload evidence" },
    { value: "confirmation",          label: "Confirmation" },
    { value: "information_response",  label: "Information response" },
];
const clientActionLabel = (v) =>
    CLIENT_ACTION_TYPES.find((o) => o.value === v)?.label || (v || "").replace(/_/g, " ") || "Acknowledgement";

// V2 Final UX §2 — scope variable input types.
const SCOPE_VAR_TYPES = [
    { value: "string",     label: "Free text" },
    { value: "number",     label: "Number" },
    { value: "enum",       label: "Choice list" },
    { value: "boolean",    label: "Yes / No" },
    { value: "date",       label: "Date" },
];
const scopeTypeLabel = (v) => SCOPE_VAR_TYPES.find((o) => o.value === v)?.label || "Free text";

// V2 Final UX §3 — prerequisite timings.
const PREREQ_TIMINGS = [
    { value: "Activation Required", label: "Required before activation" },
    { value: "Required Later",      label: "Required later" },
];
const prereqTimingLabel = (v) =>
    PREREQ_TIMINGS.find((o) => o.value === v)?.label || "Required before activation";

// V2 Final UX §4 — deliverable categories.
const DELIVERABLE_CATEGORIES = [
    "Drawings", "Report", "Submission", "Statutory", "Design pack",
    "Correspondence", "Other",
];
const VISIBILITY_CHOICES = [
    { value: "client_visible", label: "Client visible" },
    { value: "internal_only",  label: "Internal only" },
];
const visibilityLabel = (v) => VISIBILITY_CHOICES.find(o => o.value === v)?.label || "Client visible";

// V2 Final UX §5 — internal-task default owners.
const TASK_OWNERS = ["Project Owner", "Package Owner", "Project Team"];

// V2 Final UX §7 — programme item types.
const PROGRAMME_TYPES = ["Task", "Milestone"];

// V2 Final UX §8 — commercial multi-select vocabulary.
const PRICING_METHODS = [
    "Fixed Fee", "Time Charge", "Unit-Based", "Monthly Retainer",
    "Lump Sum / Contract Sum", "Custom",
];
const CONTRACT_BASES = [
    "LRS Paid Services Appointment",
    "LRS Free Design Package Appointment",
    "Bespoke Contract",
    "Client-Provided Contract",
];

// ---------- Small UI primitives ----------
const Field = ({ label, children, hint, testid }) => (
    <label className="flex flex-col gap-1 text-[12px]" data-testid={testid}>
        <span className="text-[#333] font-medium">{label}</span>
        {children}
        {hint && <span className="text-[11px] text-[#888]">{hint}</span>}
    </label>);
const Input = (props) => (
    <input {...props}
           className={`border border-[#E5E2DC] rounded px-2 py-1 text-[13px] ${props.className || ""}`} />);
const TextArea = (props) => (
    <textarea rows={props.rows || 3} {...props}
              className={`border border-[#E5E2DC] rounded px-2 py-1 text-[13px] ${props.className || ""}`} />);
const Select = (props) => (
    <select {...props}
            className={`border border-[#E5E2DC] rounded px-2 py-1 text-[13px] ${props.className || ""}`}>
        {props.children}
    </select>);
const Card = ({ title, testid, id, subtitle, children }) => (
    <section id={id} className="border border-[#E5E2DC] rounded bg-white p-4 scroll-mt-24" data-testid={testid}>
        <h3 className="font-manrope text-[15px] font-semibold mb-1">{title}</h3>
        {subtitle && <p className="text-[12px] text-[#666] mb-3">{subtitle}</p>}
        {children}
    </section>);
const Pill = ({ tone = "neutral", children }) => {
    const cls = tone === "good" ? "bg-[#E4F0E4] text-[#2E5C2E]"
              : tone === "warn" ? "bg-[#F9E8D3] text-[#7A4A17]"
              : tone === "draft" ? "bg-[#EAE6DE] text-[#5A503C]"
              : "bg-[#F3F0EA] text-[#3F3A2E]";
    return <span className={`inline-block px-2 py-0.5 rounded text-[11px] ${cls}`}>{children}</span>;
};

// Toggle pill for multi-select (§8).
const TogglePill = ({ on, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`text-[12px] px-3 py-1.5 rounded-full border transition ${on
                ? "bg-charcoal text-white border-charcoal"
                : "bg-white text-charcoal border-[#DDD8D0] hover:border-charcoal/40"}`}>
        {children}
    </button>);

// ---------- Utility ----------
function slug(s) {
    return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 48);
}
function safeStr(x) {
    if (x == null) return "";
    if (typeof x === "string") return x;
    if (typeof x === "number" || typeof x === "boolean") return String(x);
    // §11 [object Object] fix — never stringify raw objects into the DOM.
    // Callers should have picked a specific field; if they land here,
    // return "" so nothing dodgy renders.
    return "";
}

// Human item-editor: array of arbitrary shape, with a per-row
// renderer.  Preserves any keys the caller doesn't touch.
function ItemEditor({ testid, rows, blank, onChange, render, addLabel, emptyLabel }) {
    const add = () => onChange([...(rows || []), { ...blank }]);
    const patchRow = (i, patch) =>
        onChange((rows || []).map((r, ix) => ix === i ? { ...r, ...patch } : r));
    const remove = (i) => onChange((rows || []).filter((_, ix) => ix !== i));
    return (
        <div className="flex flex-col gap-2" data-testid={testid}>
            {(!rows || rows.length === 0) && (
                <p className="text-[12px] text-[#888]" data-testid={`${testid}-empty`}>
                    {emptyLabel || "None yet."}
                </p>)}
            {(rows || []).map((row, i) => (
                <div key={i} className="border border-[#EEE9E0] rounded p-3 grid grid-cols-12 gap-3 items-start"
                     data-testid={`${testid}-row-${i}`}>
                    {render(row, (patch) => patchRow(i, patch), i)}
                    <button type="button" onClick={() => remove(i)}
                            className="col-span-1 text-[16px] text-[#B5866A] hover:text-[#A8543F] justify-self-end"
                            title="Remove"
                            data-testid={`${testid}-row-${i}-remove`}>×</button>
                </div>))}
            <button type="button" onClick={add}
                    className="text-[12px] font-semibold text-[#A8543F] hover:text-[#8B4535] self-start"
                    data-testid={`${testid}-add`}>+ {addLabel || "Add"}</button>
        </div>);
}

// ---------- Sections config ----------
const V2_SECTIONS = [
    { id: "sec-basic",         label: "Basic details" },
    { id: "sec-scope",         label: "Scope" },
    { id: "sec-prereq",        label: "Before this package can start" },
    { id: "sec-deliverables",  label: "Deliverables" },
    { id: "sec-tasks",         label: "Internal tasks" },
    { id: "sec-client",        label: "Client actions" },
    { id: "sec-programme",     label: "Programme" },
    { id: "sec-commercial",    label: "Commercial setup" },
    { id: "sec-rules",         label: "Activation & completion" },
    { id: "sec-visibility",    label: "Client visibility" },
    { id: "sec-advanced",      label: "Advanced / technical" },
    { id: "sec-publish",       label: "Review & publish" },
];

// ---------- Main page ----------
export default function TemplateEditorPage() {
    const { code, draftId } = useParams();
    const [sp] = useSearchParams();
    const mode = sp.get("mode") || (draftId ? "resume"
                                    : code ? "edit_from_published"
                                    : "create_scratch");
    const modeTitle = mode === "resume" ? "Resume draft"
                    : mode === "edit_from_published" ? "Edit package"
                    : "New package";
    usePageMeta({ title: `${modeTitle} · Service Packages | Studio G4 Admin` });
    const nav = useNavigate();

    const [draft, setDraft] = useState(null);
    const [seed, setSeed] = useState(null);   // baseline for change summary
    const [saving, setSaving] = useState(false);
    const [pubBusy, setPubBusy] = useState(false);
    const [ackChecked, setAckChecked] = useState(false);
    const [err, setErr] = useState("");
    const [notice, setNotice] = useState("");

    // ---- load / create draft ----
    useEffect(() => {
        (async () => {
            try {
                if (mode === "resume") {
                    const r = await axios.get(`${API}/admin/service-packages/drafts/${draftId}`, { withCredentials: true });
                    setDraft(r.data);
                    if (r.data?.seed_from_version_id) {
                        try {
                            const s = await axios.get(`${API}/admin/service-packages/versions/${r.data.seed_from_version_id}`, { withCredentials: true });
                            setSeed(s.data);
                        } catch { /* summary shows "new template" */ }
                    }
                } else if (mode === "edit_from_published") {
                    const r = await axios.post(`${API}/admin/service-packages/drafts`,
                        { from_package_code: code, create_op_id: `ui-editdraft-${code}-${Date.now()}` },
                        { withCredentials: true });
                    setDraft(r.data);
                    nav(`/admin/service-packages/drafts/${r.data.id}`, { replace: true });
                }
            } catch (e2) { setErr(e2?.response?.data?.error || e2.message); }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, code, draftId]);

    const patch = useCallback(async (fields) => {
        if (!draft) return;
        setSaving(true); setErr("");
        try {
            const r = await axios.patch(`${API}/admin/service-packages/drafts/${draft.id}`, fields, { withCredentials: true });
            setDraft(r.data);
        } catch (e2) { setErr(e2?.response?.data?.error || e2.message); }
        finally { setSaving(false); }
    }, [draft]);

    const publish = async () => {
        if (!draft) return;
        if (!ackChecked) { setErr("please confirm the acknowledgement before publishing"); return; }
        setPubBusy(true); setErr("");
        try {
            const r = await axios.post(`${API}/admin/service-packages/drafts/${draft.id}/publish`,
                { publication_op_id: `ui-pub-${draft.id}-${Date.now()}`, existing_instances_ack: true, catalogue_state: "Active" },
                { withCredentials: true });
            setNotice(`Published version ${r.data.version} of ${r.data.package_code}.`);
            const rd = await axios.get(`${API}/admin/service-packages/drafts/${draft.id}`, { withCredentials: true });
            setDraft(rd.data);
        } catch (e2) { setErr(e2?.response?.data?.error || e2.message); }
        finally { setPubBusy(false); }
    };

    if (mode === "create_scratch" && !draft) {
        return <ScratchStart onCreated={(d) => { setDraft(d); nav(`/admin/service-packages/drafts/${d.id}`, { replace: true }); }} />;
    }
    if (err && !draft) return <div className="p-4 text-red-700 text-[13px]" data-testid="ted-error">{err}</div>;
    if (!draft) return <div className="p-4 text-[13px] text-[#666]" data-testid="ted-loading">Loading draft…</div>;

    const isPublished = draft.status === "Published";
    const disabled = isPublished;

    return (
        <div className="flex flex-col gap-4" data-testid="template-editor-page">
            <header className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <Link to="/admin/service-packages" className="text-[12px] underline text-[#A8543F]" data-testid="ted-back">Service Packages</Link>
                        <span className="text-[12px] text-[#888]">/</span>
                        <span className="text-[12px] text-[#333]">Template editor</span>
                    </div>
                    <h1 className="font-manrope text-2xl font-semibold text-charcoal">
                        {draft.package_name || "Untitled template"}
                    </h1>
                    <div className="flex items-center gap-2 mt-1" data-testid="ted-meta">
                        <Pill tone={draft.template_kind === "Standard" ? "warn" : "neutral"}>{draft.template_kind || "—"}</Pill>
                        <Pill tone={isPublished ? "good" : "draft"}>
                            {isPublished ? `Published v${draft.published_version}` : "Draft"}
                        </Pill>
                        {draft.seed_from_version && (
                            <span className="text-[11px] text-[#888]" data-testid="ted-seed-version">seeded from v{draft.seed_from_version}</span>)}
                        {saving && <span className="text-[11px] text-[#666]" data-testid="ted-saving">saving…</span>}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                    {notice && <span className="text-[12px] text-green-800" data-testid="ted-notice">{notice}</span>}
                    {err && <span className="text-[12px] text-red-700" data-testid="ted-err">{err}</span>}
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
                <aside className="lg:sticky lg:top-4 self-start" data-testid="ted-section-nav">
                    <nav className="flex lg:flex-col gap-1 flex-wrap">
                        {V2_SECTIONS.map((s, i) => (
                            <a key={s.id} href={`#${s.id}`} data-testid={`ted-navlink-${s.id}`}
                               className="text-[13px] px-3 py-1.5 rounded hover:bg-[#faf9f7] text-charcoal border border-transparent hover:border-[#DDD8D0] font-manrope font-semibold">
                                <span className="text-charcoal/40 mr-2">{String(i + 1).padStart(2, "0")}</span>{s.label}
                            </a>))}
                    </nav>
                </aside>
                <div className="flex flex-col gap-4">
                    <BasicDetailsSection draft={draft} setDraft={setDraft} patch={patch} disabled={disabled} />
                    <ScopeSection draft={draft} setDraft={setDraft} patch={patch} disabled={disabled} />
                    <PrerequisitesSection draft={draft} patch={patch} disabled={disabled} />
                    <DeliverablesSection draft={draft} patch={patch} disabled={disabled} />
                    <TasksSection draft={draft} patch={patch} disabled={disabled} />
                    <ClientActionsSection draft={draft} patch={patch} disabled={disabled} />
                    <ProgrammeSection draft={draft} patch={patch} disabled={disabled} />
                    <CommercialSection draft={draft} setDraft={setDraft} patch={patch} disabled={disabled} />
                    <RulesSection draft={draft} patch={patch} disabled={disabled} />
                    <VisibilitySection draft={draft} patch={patch} disabled={disabled} />
                    <AdvancedTechnicalSection draft={draft} />
                    <PublishSection draft={draft} seed={seed} isPublished={isPublished}
                                    ackChecked={ackChecked} setAckChecked={setAckChecked}
                                    publish={publish} pubBusy={pubBusy} />
                </div>
            </div>
        </div>);
}

// ---------- Sections ----------
function BasicDetailsSection({ draft, setDraft, patch, disabled }) {
    const setLocal = (k, v) => setDraft({ ...draft, [k]: v });
    return (
        <Card id="sec-basic" testid="ted-identity"
              title="1. Basic details"
              subtitle="Describe the package in business terms. Technical identifiers are generated automatically and remain immutable once published.">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Package name" testid="ted-fld-name">
                    <Input value={draft.package_name || ""}
                           onChange={(e) => setLocal("package_name", e.target.value)}
                           onBlur={(e) => e.target.value !== draft.package_name && patch({ package_name: e.target.value })}
                           disabled={disabled} data-testid="ted-inp-name" />
                </Field>
                <Field label="Package type" hint="Immutable" testid="ted-fld-kind">
                    <Input value={draft.template_kind || ""} disabled data-testid="ted-inp-kind" />
                </Field>
                <Field label="Lifecycle availability" testid="ted-fld-lc">
                    <Select value={draft.default_lifecycle_context || ""} onChange={(e) => patch({ default_lifecycle_context: e.target.value })}
                            disabled={disabled} data-testid="ted-inp-lc">
                        <option value="">—</option>
                        {["Pre-WS6","WS6","WS7","Any Workstage"].map((v) => <option key={v} value={v}>{v}</option>)}
                    </Select>
                </Field>
                <Field label="Typical Workstage" testid="ted-fld-ws">
                    <Select value={draft.typical_workstage ?? ""} onChange={(e) => patch({ typical_workstage: e.target.value ? Number(e.target.value) : null })}
                            disabled={disabled} data-testid="ted-inp-ws">
                        <option value="">—</option>
                        {[1,2,3,4,5,6,7].map((w) => <option key={w} value={w}>WS{w}</option>)}
                    </Select>
                </Field>
                <Field label="Purpose" testid="ted-fld-purpose">
                    <TextArea value={draft.purpose || ""}
                              onChange={(e) => setLocal("purpose", e.target.value)}
                              onBlur={(e) => e.target.value !== (draft.purpose || "") && patch({ purpose: e.target.value })}
                              disabled={disabled} data-testid="ted-inp-purpose" />
                </Field>
                <div /> {/* filler for 2-col grid */}
            </div>
            <details className="mt-3 text-[12px] text-[#555]" data-testid="ted-basic-tech">
                <summary className="cursor-pointer">Advanced / technical details</summary>
                <p className="mt-1">Package code: <code>{draft.package_code}</code> · Template version: v{draft.published_version || "draft"} · immutable identity.</p>
            </details>
        </Card>);
}

function ScopeSection({ draft, setDraft, patch, disabled }) {
    // Scope is stored as string; keep textarea.  scope_variables are
    // presented with humanised columns (Label / Input type / Default),
    // preserving the underlying `key` in a hidden slug.
    return (
        <Card id="sec-scope" testid="ted-scope"
              title="2. Scope"
              subtitle="Set what is included, excluded and any project-specific choices the team may need when appointing this package.">
            <div className="grid gap-3">
                <Field label="Included services" testid="ted-fld-included">
                    <TextArea value={safeStr(draft.standard_scope)}
                              onChange={(e) => setDraft({ ...draft, standard_scope: e.target.value })}
                              onBlur={(e) => e.target.value !== (draft.standard_scope || "") && patch({ standard_scope: e.target.value })}
                              disabled={disabled} data-testid="ted-inp-scope" />
                </Field>
                <Field label="Exclusions" testid="ted-fld-excl">
                    <TextArea value={safeStr(draft.exclusions)}
                              onChange={(e) => setDraft({ ...draft, exclusions: e.target.value })}
                              onBlur={(e) => e.target.value !== (draft.exclusions || "") && patch({ exclusions: e.target.value })}
                              disabled={disabled} data-testid="ted-inp-excl" />
                </Field>
                <div>
                    <div className="text-[12px] font-semibold mb-1">Project-specific choices</div>
                    <ItemEditor
                        testid="ted-scope-vars"
                        rows={draft.scope_variables || []}
                        blank={{ key: "", type: "string", default: "" }}
                        onChange={(v) => patch({ scope_variables: v.map(r => ({ ...r, key: r.key || slug(r.label || "") })) })}
                        emptyLabel="No project-specific choices yet."
                        addLabel="Add project-specific choice"
                        render={(row, set) => (<>
                            <div className="col-span-5">
                                <Field label="Label" testid="ted-scope-var-label">
                                    <Input disabled={disabled}
                                           value={row.label || row.key || ""}
                                           onChange={(e) => set({ label: e.target.value, key: row.key || slug(e.target.value) })}
                                           data-testid="ted-scope-var-label-inp" />
                                </Field>
                            </div>
                            <div className="col-span-3">
                                <Field label="Input type" testid="ted-scope-var-type">
                                    <Select disabled={disabled} value={row.type || "string"}
                                            onChange={(e) => set({ type: e.target.value })}
                                            data-testid="ted-scope-var-type-inp">
                                        {SCOPE_VAR_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </Select>
                                </Field>
                            </div>
                            <div className="col-span-3">
                                <Field label="Optional default" testid="ted-scope-var-default">
                                    <Input disabled={disabled} value={safeStr(row.default)}
                                           onChange={(e) => set({ default: e.target.value })}
                                           data-testid="ted-scope-var-default-inp"
                                           placeholder="e.g. Concept" />
                                </Field>
                            </div>
                        </>)} />
                </div>
            </div>
        </Card>);
}

function PrerequisitesSection({ draft, patch, disabled }) {
    return (
        <Card id="sec-prereq" testid="ted-prereq"
              title="3. Before this package can start"
              subtitle="List prerequisites in plain English and say whether each one blocks activation or may be completed later.">
            <ItemEditor
                testid="ted-prereq-list"
                rows={draft.prerequisites || []}
                blank={{ item_id: "", title: "", timing: "Activation Required", source_context: "" }}
                onChange={(v) => patch({ prerequisites: v.map(r => ({ ...r, item_id: r.item_id || slug(r.title || "prereq") })) })}
                emptyLabel="No prerequisites yet."
                addLabel="Add prerequisite"
                render={(row, set) => (<>
                    <div className="col-span-5">
                        <Field label="Requirement" testid="ted-prereq-title">
                            <Input disabled={disabled}
                                   value={safeStr(row.title || row.description)}
                                   onChange={(e) => set({ title: e.target.value })}
                                   data-testid="ted-prereq-title-inp"
                                   placeholder="e.g. Measured survey" />
                        </Field>
                    </div>
                    <div className="col-span-3">
                        <Field label="Timing" testid="ted-prereq-timing">
                            <Select disabled={disabled}
                                    value={row.timing || "Activation Required"}
                                    onChange={(e) => set({ timing: e.target.value })}
                                    data-testid="ted-prereq-timing-inp">
                                {PREREQ_TIMINGS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <div className="col-span-3">
                        <Field label="Source / context" testid="ted-prereq-source">
                            <Input disabled={disabled}
                                   value={safeStr(row.source_context)}
                                   onChange={(e) => set({ source_context: e.target.value })}
                                   data-testid="ted-prereq-source-inp"
                                   placeholder="Optional" />
                        </Field>
                    </div>
                </>)} />
        </Card>);
}

function DeliverablesSection({ draft, patch, disabled }) {
    return (
        <Card id="sec-deliverables" testid="ted-deliverables"
              title="4. Deliverables"
              subtitle="Define what this package is expected to produce.">
            <ItemEditor
                testid="ted-deliverables-list"
                rows={draft.standard_deliverables || []}
                blank={{ item_id: "", title: "", category: "Drawings", client_visibility: "client_visible", required_for_completion: true }}
                onChange={(v) => patch({ standard_deliverables: v.map(r => ({ ...r, item_id: r.item_id || slug(r.title || "deliv") })) })}
                emptyLabel="No deliverables yet."
                addLabel="Add deliverable"
                render={(row, set) => (<>
                    <div className="col-span-5">
                        <Field label="Deliverable name" testid="ted-deliv-name">
                            <Input disabled={disabled}
                                   value={safeStr(row.title)}
                                   onChange={(e) => set({ title: e.target.value })}
                                   data-testid="ted-deliv-name-inp" />
                        </Field>
                    </div>
                    <div className="col-span-3">
                        <Field label="Category" testid="ted-deliv-cat">
                            <Select disabled={disabled} value={row.category || "Drawings"}
                                    onChange={(e) => set({ category: e.target.value })}
                                    data-testid="ted-deliv-cat-inp">
                                {DELIVERABLE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <div className="col-span-2">
                        <Field label="Client visibility" testid="ted-deliv-vis">
                            <Select disabled={disabled} value={row.client_visibility || "client_visible"}
                                    onChange={(e) => set({ client_visibility: e.target.value })}
                                    data-testid="ted-deliv-vis-inp">
                                {VISIBILITY_CHOICES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <div className="col-span-1 flex items-end pb-1">
                        <label className="text-[12px] flex items-center gap-1"
                               data-testid="ted-deliv-req">
                            <input type="checkbox" disabled={disabled}
                                   checked={row.required_for_completion !== false}
                                   onChange={(e) => set({ required_for_completion: e.target.checked })} />
                            Required
                        </label>
                    </div>
                </>)} />
        </Card>);
}

function TasksSection({ draft, patch, disabled }) {
    return (
        <Card id="sec-tasks" testid="ted-tasks"
              title="5. Internal tasks"
              subtitle="Default tasks created when the package activates. Existing projects are unaffected by future edits.">
            <ItemEditor
                testid="ted-tasks-list"
                rows={draft.internal_task_template || []}
                blank={{ item_id: "", title: "", default_owner: "Project Owner", required_for_workstage_completion: true }}
                onChange={(v) => patch({ internal_task_template: v.map(r => ({ ...r, item_id: r.item_id || slug(r.title || "task") })) })}
                emptyLabel="No internal tasks yet."
                addLabel="Add internal task"
                render={(row, set) => (<>
                    <div className="col-span-6">
                        <Field label="Task title" testid="ted-task-title">
                            <Input disabled={disabled} value={safeStr(row.title)}
                                   onChange={(e) => set({ title: e.target.value })}
                                   data-testid="ted-task-title-inp" />
                        </Field>
                    </div>
                    <div className="col-span-3">
                        <Field label="Default owner" testid="ted-task-owner">
                            <Select disabled={disabled} value={row.default_owner || "Project Owner"}
                                    onChange={(e) => set({ default_owner: e.target.value })}
                                    data-testid="ted-task-owner-inp">
                                {TASK_OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <div className="col-span-2 flex items-end pb-1">
                        <label className="text-[12px] flex items-center gap-1"
                               data-testid="ted-task-req">
                            <input type="checkbox" disabled={disabled}
                                   checked={row.required_for_workstage_completion !== false}
                                   onChange={(e) => set({ required_for_workstage_completion: e.target.checked })} />
                            Required for completion
                        </label>
                    </div>
                </>)} />
        </Card>);
}

function ClientActionsSection({ draft, patch, disabled }) {
    return (
        <Card id="sec-client" testid="ted-client-actions"
              title="6. Client actions"
              subtitle="Default client obligations created from this package.">
            <ItemEditor
                testid="ted-client-actions-list"
                rows={draft.client_action_template || []}
                blank={{ item_id: "", title: "", action_type: "approval_confirmation", required: true }}
                onChange={(v) => patch({ client_action_template: v.map(r => ({ ...r, item_id: r.item_id || slug(r.title || "ca") })) })}
                emptyLabel="No client actions yet."
                addLabel="Add client action"
                render={(row, set) => (<>
                    <div className="col-span-6">
                        <Field label="Action title" testid="ted-ca-title">
                            <Input disabled={disabled} value={safeStr(row.title)}
                                   onChange={(e) => set({ title: e.target.value })}
                                   data-testid="ted-ca-title-inp" />
                        </Field>
                    </div>
                    <div className="col-span-3">
                        <Field label="Response type" testid="ted-ca-type">
                            <Select disabled={disabled}
                                    value={row.action_type || "approval_confirmation"}
                                    onChange={(e) => set({ action_type: e.target.value })}
                                    data-testid="ted-ca-type-inp">
                                {CLIENT_ACTION_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <div className="col-span-2 flex items-end pb-1">
                        <label className="text-[12px] flex items-center gap-1"
                               data-testid="ted-ca-req">
                            <input type="checkbox" disabled={disabled}
                                   checked={row.required !== false}
                                   onChange={(e) => set({ required: e.target.checked })} />
                            Required
                        </label>
                    </div>
                </>)} />
        </Card>);
}

function ProgrammeSection({ draft, patch, disabled }) {
    const items = draft.programme_template?.items || [];
    const setItems = (v) => patch({
        programme_template: { ...(draft.programme_template || {}), items: v.map(r => ({ ...r, item_id: r.item_id || slug(r.title || "prog") })) },
    });
    return (
        <Card id="sec-programme" testid="ted-prog"
              title="7. Programme"
              subtitle="Default Programme items created at package activation. Mini Programmes remain one level only.">
            <ItemEditor
                testid="ted-prog-items"
                rows={items}
                blank={{ item_id: "", title: "", item_type: "Task", default_timing: "" }}
                onChange={setItems}
                emptyLabel="No Programme items yet."
                addLabel="Add Programme item"
                render={(row, set) => (<>
                    <div className="col-span-6">
                        <Field label="Programme item title" testid="ted-prog-title">
                            <Input disabled={disabled} value={safeStr(row.title)}
                                   onChange={(e) => set({ title: e.target.value })}
                                   data-testid="ted-prog-title-inp" />
                        </Field>
                    </div>
                    <div className="col-span-2">
                        <Field label="Type" testid="ted-prog-type">
                            <Select disabled={disabled} value={row.item_type || "Task"}
                                    onChange={(e) => set({ item_type: e.target.value })}
                                    data-testid="ted-prog-type-inp">
                                {PROGRAMME_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <div className="col-span-3">
                        <Field label="Default timing / duration" testid="ted-prog-timing">
                            <Input disabled={disabled} value={safeStr(row.default_timing)}
                                   onChange={(e) => set({ default_timing: e.target.value })}
                                   placeholder="e.g. 10 working days"
                                   data-testid="ted-prog-timing-inp" />
                        </Field>
                    </div>
                </>)} />
        </Card>);
}

function CommercialSection({ draft, setDraft, patch, disabled }) {
    // V2 Final UX §8 — pill multi-select for methods + basis, then
    // dropdown for the default of each.
    const spm = draft.supported_pricing_methods || [];
    const scb = draft.supported_contract_bases || [];
    const togglePricing = (m) => {
        const next = spm.includes(m) ? spm.filter(x => x !== m) : [...spm, m];
        patch({ supported_pricing_methods: next });
    };
    const toggleBasis = (b) => {
        const next = scb.includes(b) ? scb.filter(x => x !== b) : [...scb, b];
        patch({ supported_contract_bases: next });
    };
    return (
        <Card id="sec-commercial" testid="ted-pricing"
              title="8. Commercial setup"
              subtitle="Choose the commercial methods this package supports and the normal default.">
            <div className="grid gap-4">
                <div>
                    <div className="text-[12px] font-semibold mb-1">Allowed pricing methods</div>
                    <div className="flex flex-wrap gap-2" data-testid="ted-pricing-methods">
                        {PRICING_METHODS.map(m => (
                            <TogglePill key={m} on={spm.includes(m)} onClick={() => !disabled && togglePricing(m)}
                                        testid={`ted-pricing-method-${slug(m)}`}>
                                {m}
                            </TogglePill>))}
                    </div>
                </div>
                <div>
                    <div className="text-[12px] font-semibold mb-1">Supported Contract Bases</div>
                    <div className="flex flex-wrap gap-2" data-testid="ted-contract-bases">
                        {CONTRACT_BASES.map(b => (
                            <TogglePill key={b} on={scb.includes(b)} onClick={() => !disabled && toggleBasis(b)}
                                        testid={`ted-contract-basis-${slug(b)}`}>
                                {b}
                            </TogglePill>))}
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Default pricing method" testid="ted-fld-dpm">
                        <Select disabled={disabled}
                                value={draft.default_pricing_method || ""}
                                onChange={(e) => patch({ default_pricing_method: e.target.value })}
                                data-testid="ted-inp-dpm">
                            <option value="">—</option>
                            {spm.map(m => <option key={m} value={m}>{m}</option>)}
                        </Select>
                    </Field>
                    <Field label="Default Contract Basis" testid="ted-fld-dcb">
                        <Select disabled={disabled}
                                value={draft.default_contract_basis || ""}
                                onChange={(e) => patch({ default_contract_basis: e.target.value })}
                                data-testid="ted-inp-dcb">
                            <option value="">—</option>
                            {scb.map(b => <option key={b} value={b}>{b}</option>)}
                        </Select>
                    </Field>
                </div>
            </div>
        </Card>);
}

function RulesSection({ draft, patch, disabled }) {
    // V2 Final UX §9 — readable rule builders.  Stored rows may carry
    // legacy keys (item_id / condition_id / gate_id / required /
    // waivable).  We display humanised phrasing but keep the raw keys
    // intact on save (via slug fallback + preserved rest).
    const acts = draft.activation_conditions || [];
    const comps = draft.completion_conditions || [];
    const setActs = (v) => patch({ activation_conditions: v.map(r => ({
        ...r,
        item_id: r.item_id || r.condition_id || slug(r.title || r.description || "act"),
    })) });
    const setComps = (v) => patch({ completion_conditions: v.map(r => ({
        ...r,
        item_id: r.item_id || r.gate_id || slug(r.title || r.description || "comp"),
    })) });
    return (
        <Card id="sec-rules" testid="ted-rules"
              title="9. Activation & completion"
              subtitle="Use readable conditions. Gate IDs and raw schema keys stay in Advanced / technical.">
            <div className="mb-2 text-[12px] font-semibold">Activation rules</div>
            <ItemEditor
                testid="ted-activation-list"
                rows={acts}
                blank={{ item_id: "", title: "", must_be: "true", blocks_activation: true }}
                onChange={setActs}
                emptyLabel="No activation rules yet."
                addLabel="Add activation rule"
                render={(row, set) => (<>
                    <div className="col-span-7">
                        <Field label="Condition" testid="ted-act-title">
                            <Input disabled={disabled}
                                   value={safeStr(row.title || row.description)}
                                   onChange={(e) => set({ title: e.target.value })}
                                   placeholder="e.g. Coordinated Technical Design is available"
                                   data-testid="ted-act-title-inp" />
                        </Field>
                    </div>
                    <div className="col-span-2">
                        <Field label="State" testid="ted-act-state">
                            <Select disabled={disabled} value={row.must_be ?? "true"}
                                    onChange={(e) => set({ must_be: e.target.value })}
                                    data-testid="ted-act-state-inp">
                                <option value="true">Must be true</option>
                                <option value="false">Must be false</option>
                            </Select>
                        </Field>
                    </div>
                    <div className="col-span-2">
                        <Field label="Effect" testid="ted-act-effect">
                            <Select disabled={disabled}
                                    value={row.blocks_activation === false ? "advisory" : "blocks"}
                                    onChange={(e) => set({ blocks_activation: e.target.value === "blocks" })}
                                    data-testid="ted-act-effect-inp">
                                <option value="blocks">Blocks activation</option>
                                <option value="advisory">Advisory only</option>
                            </Select>
                        </Field>
                    </div>
                </>)} />

            <div className="mt-4 mb-2 text-[12px] font-semibold">Completion rules</div>
            <ItemEditor
                testid="ted-completion-list"
                rows={comps}
                blank={{ item_id: "", title: "", waivable: false }}
                onChange={setComps}
                emptyLabel="No completion rules yet."
                addLabel="Add completion rule"
                render={(row, set) => (<>
                    <div className="col-span-8">
                        <Field label="Requirement" testid="ted-comp-title">
                            <Input disabled={disabled}
                                   value={safeStr(row.title || row.description)}
                                   onChange={(e) => set({ title: e.target.value })}
                                   placeholder="e.g. Submission pack delivered"
                                   data-testid="ted-comp-title-inp" />
                        </Field>
                    </div>
                    <div className="col-span-3">
                        <Field label="Waivability" testid="ted-comp-waiv">
                            <Select disabled={disabled}
                                    value={row.waivable ? "waivable" : "non_waivable"}
                                    onChange={(e) => set({ waivable: e.target.value === "waivable" })}
                                    data-testid="ted-comp-waiv-inp">
                                <option value="non_waivable">Non-waivable</option>
                                <option value="waivable">Waivable</option>
                            </Select>
                        </Field>
                    </div>
                </>)} />
        </Card>);
}

function VisibilitySection({ draft, patch, disabled }) {
    const cvd = draft.client_visibility_defaults || {};
    const setKey = (k, v) => patch({ client_visibility_defaults: { ...cvd, [k]: v } });
    // Legacy shape may store booleans (true/false) for granular keys
    // like programme/mini_programme/deliverables/client_actions.  UI
    // exposes two consolidated choices.
    const toChoice = (bool) => bool === false ? "internal_only" : "client_visible";
    const fromChoice = (choice) => choice === "internal_only" ? false : true;
    return (
        <Card id="sec-visibility" testid="ted-cvd"
              title="10. Client visibility"
              subtitle="Set sensible defaults. Runtime records still obey their own accepted visibility rules.">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Default Programme visibility" testid="ted-fld-cvd-prog">
                    <Select disabled={disabled}
                            value={toChoice(cvd.programme_items !== undefined ? cvd.programme_items : cvd.programme)}
                            onChange={(e) => setKey("programme_items", fromChoice(e.target.value))}
                            data-testid="ted-inp-cvd-prog">
                        {VISIBILITY_CHOICES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                </Field>
                <Field label="Default deliverable visibility" testid="ted-fld-cvd-del">
                    <Select disabled={disabled}
                            value={toChoice(cvd.deliverables)}
                            onChange={(e) => setKey("deliverables", fromChoice(e.target.value))}
                            data-testid="ted-inp-cvd-del">
                        {VISIBILITY_CHOICES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                </Field>
            </div>
        </Card>);
}

function AdvancedTechnicalSection({ draft }) {
    // V2 Final UX §10 — collapsed disclosure holds the raw schema for
    // authorised inspection.  NOT part of the normal page flow.
    return (
        <Card id="sec-advanced" testid="ted-advanced"
              title="11. Advanced / technical"
              subtitle="Raw schema is available for inspection but is not needed for normal editing.">
            <details data-testid="ted-adv-details">
                <summary className="cursor-pointer text-[13px] font-semibold text-[#A8543F]">
                    Show advanced / technical details
                </summary>
                <div className="mt-2 text-[11px] space-y-2">
                    <div><strong>Package code:</strong> <code>{draft.package_code}</code> · <strong>Version:</strong> v{draft.published_version || "draft"}</div>
                    <div><strong>scope_variables (raw):</strong> <pre className="bg-[#F8F5EF] p-2 rounded overflow-x-auto">{JSON.stringify(draft.scope_variables || [], null, 2)}</pre></div>
                    <div><strong>required_client_inputs (raw):</strong> <pre className="bg-[#F8F5EF] p-2 rounded overflow-x-auto">{JSON.stringify(draft.required_client_inputs || [], null, 2)}</pre></div>
                    <div><strong>activation_conditions (raw):</strong> <pre className="bg-[#F8F5EF] p-2 rounded overflow-x-auto">{JSON.stringify(draft.activation_conditions || [], null, 2)}</pre></div>
                    <div><strong>completion_conditions (raw):</strong> <pre className="bg-[#F8F5EF] p-2 rounded overflow-x-auto">{JSON.stringify(draft.completion_conditions || [], null, 2)}</pre></div>
                    <div><strong>client_visibility_defaults (raw):</strong> <pre className="bg-[#F8F5EF] p-2 rounded overflow-x-auto">{JSON.stringify(draft.client_visibility_defaults || {}, null, 2)}</pre></div>
                </div>
            </details>
        </Card>);
}

function PublishSection({ draft, seed, isPublished, ackChecked, setAckChecked, publish, pubBusy }) {
    const changes = useMemo(() => buildChangeSummary(seed, draft), [seed, draft]);
    return (
        <Card id="sec-publish" testid="ted-publish-card"
              title={isPublished ? "12. Review & publish (already published)" : "12. Review & publish"}
              subtitle="Publishing creates a new immutable template version. Existing project package instances stay bound to their existing version.">
            <div className="border border-[#EADBD4] bg-[#FBF7F4] rounded p-3" data-testid="ted-change-summary">
                <div className="text-[12px] font-semibold mb-1">Summary of changes</div>
                {changes.length === 0 ? (
                    <div className="text-[12px] text-[#666]" data-testid="ted-change-empty">
                        No differences detected against the seed version.
                    </div>
                ) : (
                    <ul className="text-[12px] list-disc pl-4 space-y-0.5" data-testid="ted-change-list">
                        {changes.map((c, i) => <li key={i} data-testid={`ted-change-item-${i}`}>{c}</li>)}
                    </ul>)}
            </div>
            <label className="flex items-center gap-2 text-[12px] mt-3 mb-2" data-testid="ted-ack-row">
                <input type="checkbox" checked={ackChecked}
                       onChange={(e) => setAckChecked(e.target.checked)}
                       disabled={isPublished}
                       data-testid="ted-ack-checkbox" />
                I confirm existing project package instances will <b>not</b> be updated by this publish.
            </label>
            <div className="flex items-center gap-2">
                <button type="button" onClick={publish}
                        disabled={pubBusy || isPublished || !ackChecked}
                        className="bg-[#A8543F] text-white text-[13px] font-semibold px-4 py-2 rounded disabled:opacity-40"
                        data-testid="ted-publish-btn">
                    {pubBusy ? "Publishing…" : isPublished ? "Published" : "Publish new version"}
                </button>
                {isPublished && (
                    <span className="text-[12px] text-[#666]" data-testid="ted-published-info">
                        Published version v{draft.published_version} · {(draft.published_at || "").slice(0, 19)}
                    </span>)}
            </div>
        </Card>);
}

// Build change summary between two versions (seed vs current draft).
function buildChangeSummary(seed, draft) {
    if (!seed || !draft) return [];
    const out = [];
    if ((seed.package_name || "") !== (draft.package_name || ""))
        out.push(`Package name changed to "${draft.package_name}"`);
    if ((seed.purpose || "") !== (draft.purpose || ""))
        out.push("Purpose updated");
    if ((seed.standard_scope || "") !== (draft.standard_scope || ""))
        out.push("Included services updated");
    if ((seed.exclusions || "") !== (draft.exclusions || ""))
        out.push("Exclusions updated");
    const seedTitles = (arr, key = "title") => (arr || []).map(r => r[key] || "").sort().join("¦");
    const cmpList = (a, b, label) => {
        if (seedTitles(a) !== seedTitles(b)) out.push(`${label} updated`);
    };
    cmpList(seed.prerequisites, draft.prerequisites, "Prerequisites");
    cmpList(seed.standard_deliverables, draft.standard_deliverables, "Deliverables");
    cmpList(seed.internal_task_template, draft.internal_task_template, "Internal tasks");
    cmpList(seed.client_action_template, draft.client_action_template, "Client actions");
    cmpList(seed.programme_template?.items, draft.programme_template?.items, "Programme items");
    if ((seed.default_pricing_method || "") !== (draft.default_pricing_method || ""))
        out.push(`Default pricing method → ${draft.default_pricing_method || "—"}`);
    if ((seed.default_contract_basis || "") !== (draft.default_contract_basis || ""))
        out.push(`Default Contract Basis → ${draft.default_contract_basis || "—"}`);
    const seedSet = (arr) => (arr || []).slice().sort().join("¦");
    if (seedSet(seed.supported_pricing_methods) !== seedSet(draft.supported_pricing_methods))
        out.push("Allowed pricing methods updated");
    if (seedSet(seed.supported_contract_bases) !== seedSet(draft.supported_contract_bases))
        out.push("Supported Contract Bases updated");
    return out;
}

// ---------- Scratch Start ----------
function ScratchStart({ onCreated }) {
    const [pkgCode, setPkgCode] = useState("");
    const [name, setName] = useState("");
    const [kind, setKind] = useState("Custom Global");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const create = async () => {
        setBusy(true); setErr("");
        try {
            const r = await axios.post(`${API}/admin/service-packages/drafts`,
                { new_package_code: pkgCode, new_package_name: name, new_template_kind: kind,
                  create_op_id: `ui-scratch-${pkgCode}-${Date.now()}` },
                { withCredentials: true });
            onCreated(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
        finally { setBusy(false); }
    };
    return (
        <div className="p-4" data-testid="ted-scratch">
            <h1 className="font-manrope text-2xl font-semibold text-charcoal mb-2">New Service Package template</h1>
            <p className="text-[13px] text-[#666] mb-4">
                Package Code and Template Type are immutable once created. Choose them carefully.
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-xl">
                <Field label="Package Code" testid="ted-sc-code">
                    <Input value={pkgCode} onChange={(e) => setPkgCode(e.target.value)} data-testid="ted-sc-code-inp" />
                </Field>
                <Field label="Template Type" testid="ted-sc-kind">
                    <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="ted-sc-kind-inp">
                        {["Standard", "Custom Global", "Custom Bespoke"].map(v => <option key={v} value={v}>{v}</option>)}
                    </Select>
                </Field>
                <Field label="Package Name" testid="ted-sc-name">
                    <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ted-sc-name-inp" />
                </Field>
            </div>
            <div className="mt-3 flex gap-2">
                <button type="button" onClick={create} disabled={busy || !pkgCode || !name}
                        className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded disabled:opacity-40"
                        data-testid="ted-sc-create-btn">
                    {busy ? "Creating…" : "Create draft"}
                </button>
            </div>
            {err && <div className="mt-2 text-[12px] text-red-700" data-testid="ted-sc-err">{err}</div>}
        </div>);
}
